'use client';

import { useEffect, useState } from 'react';
import type { PickupPointLocation } from 'services';
import { PICKUP_POINT_INPUT_NAME } from '../constants';
import { getNextOptionIndex, getPickupPointOptionId, getPickupPointText } from '../utils';

interface UsePickupPointKeyboardNavigationOptions {
  locations: PickupPointLocation[];
  selectedStation: string;
  onSelect: (location: PickupPointLocation) => void;
}

// Targets the visible search field: on mobile the aria-hidden trigger shares the name and comes first in the DOM.
const SEARCH_INPUT_SELECTOR = `input[name="${PICKUP_POINT_INPUT_NAME}"]:not([aria-hidden="true"])`;

/**
 * Keyboard navigation for the custom options slot, which ComboBox doesn't manage itself.
 * Only the user's navigation is state; without it the active option is the selected one, else the first.
 * @returns The currently active location, if any.
 */
export function usePickupPointKeyboardNavigation({
  locations,
  selectedStation,
  onSelect
}: UsePickupPointKeyboardNavigationOptions) {
  const [navigatedServiceId, setNavigatedServiceId] = useState<string>();
  const activeLocation =
    locations.find(location => location.serviceId === navigatedServiceId) ??
    locations.find(location => getPickupPointText(location) === selectedStation) ??
    locations[0];
  const activeIndex = activeLocation ? locations.indexOf(activeLocation) : -1;
  const activeOptionId = activeLocation ? getPickupPointOptionId(activeLocation) : undefined;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (locations.length === 0) {
        return;
      }

      if (event.key === 'Enter') {
        if (activeLocation) {
          event.preventDefault();
          onSelect(activeLocation);
        }

        return;
      }

      const nextIndex = getNextOptionIndex(event.key, activeIndex, locations.length);

      if (nextIndex === undefined) {
        return;
      }

      event.preventDefault();
      setNavigatedServiceId(locations[nextIndex]?.serviceId);
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, activeLocation, locations, onSelect]);

  // ComboBox can't set this itself: its own options list is empty because of the custom slot.
  useEffect(() => {
    if (!activeOptionId) {
      return;
    }

    const searchInput = document.querySelector(SEARCH_INPUT_SELECTOR);

    searchInput?.setAttribute('aria-activedescendant', activeOptionId);

    return () => searchInput?.removeAttribute('aria-activedescendant');
  }, [activeOptionId]);

  useEffect(() => {
    if (activeOptionId) {
      document.getElementById(activeOptionId)?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeOptionId]);

  return activeLocation;
}
